import type { IndicatorOut } from '@/api/schema';

/**
 * The indicator picker's search: what a query matches, and in what order — the web's copy of the
 * phone's `indicator_search.dart`, with the same tiers in the same order (`indicator_strategy.md`
 * §9), so "FVG", "fibonacci" and "SMC" find the same indicators on both.
 *
 * **One list, strongest match first.** Exact name, then name prefix, then name or type key
 * contains, then a search term, a concept, the category, the description. Within a tier the input
 * order is kept.
 */

/** Lowercased, underscores to spaces, the punctuation a user may or may not type dropped. `%`, `/`,
 * `+` and `-` survive: they carry meaning in a name ("Williams %R", "Premium / Discount"). */
export function normaliseSearch(text: string): string {
  return text
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/[.,'"()]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export const SEARCH_TIERS = ['exactName', 'namePrefix', 'nameContains', 'term', 'concept', 'category', 'description'] as const;
export type SearchTier = (typeof SEARCH_TIERS)[number];

/** The tier `query` (already normalised, non-empty) reaches on `indicator`, or null. */
export function searchTierOf(indicator: IndicatorOut, query: string): SearchTier | null {
  const name = normaliseSearch(indicator.display_name);
  if (name === query) return 'exactName';
  if (name.startsWith(query)) return 'namePrefix';
  if (name.includes(query) || normaliseSearch(indicator.indicator_type).includes(query)) return 'nameContains';
  if ((indicator.search_terms ?? []).some((t) => normaliseSearch(t).includes(query))) return 'term';
  if ((indicator.concepts ?? []).some((c) => normaliseSearch(c).includes(query))) return 'concept';
  if (normaliseSearch(indicator.category).includes(query)) return 'category';
  if (normaliseSearch(indicator.description).includes(query)) return 'description';
  return null;
}

/** The indicators `query` reaches, strongest first and input order within a tier. An empty query
 * returns the input unchanged. */
export function searchIndicators(indicators: IndicatorOut[], query: string): IndicatorOut[] {
  const q = normaliseSearch(query);
  if (!q) return indicators;
  return indicators
    .map((indicator, index) => ({ indicator, index, tier: searchTierOf(indicator, q) }))
    .filter((h): h is { indicator: IndicatorOut; index: number; tier: SearchTier } => h.tier !== null)
    .sort((a, b) => SEARCH_TIERS.indexOf(a.tier) - SEARCH_TIERS.indexOf(b.tier) || a.index - b.index)
    .map((h) => h.indicator);
}

/** The catalogue's categories, in the order the picker lists them; anything new goes after. */
export const CATEGORY_ORDER = ['trend', 'momentum', 'volatility', 'volume', 'support_resistance', 'price_action', 'patterns', 'price'] as const;

export const CATEGORY_LABEL: Record<string, string> = {
  trend: 'Trend',
  momentum: 'Momentum',
  volatility: 'Volatility',
  volume: 'Volume',
  support_resistance: 'Support & resistance',
  price_action: 'Price action',
  patterns: 'Chart patterns',
  price: 'Price',
};

export const categoryLabel = (category: string) =>
  CATEGORY_LABEL[category] ?? category.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

export type PickerRow = { kind: 'header'; category: string } | { kind: 'indicator'; indicator: IndicatorOut };

/**
 * The rows the picker lists. `all`: one flat list — A to Z with no query, ranked with one.
 * `category`: a header row per category, its indicators under it — A to Z in the fixed category
 * order with no query; with one, in rank order, categories ordered by their best match and those
 * with no match left out. `favourites`: the flat list cut to the starred set, and the whole
 * ranked list once there is a query.
 */
export type PickerMode = 'all' | 'category' | 'favourites';

export function pickerRows(
  indicators: IndicatorOut[],
  query: string,
  mode: PickerMode,
  favourites: ReadonlySet<string> = new Set(),
): PickerRow[] {
  const alphabetical = [...indicators].sort((a, b) => a.display_name.localeCompare(b.display_name));
  const matched = searchIndicators(alphabetical, query);
  // Favourites filters the flat list, and only without a query: a search ranks the whole
  // catalogue whichever view is selected, otherwise a user on Favourites who types "rsi" sees
  // nothing and is left wondering where RSI went.
  if (mode === 'favourites') {
    const shown = normaliseSearch(query) ? matched : matched.filter((i) => favourites.has(i.indicator_type));
    return shown.map((indicator) => ({ kind: 'indicator', indicator }));
  }
  if (mode === 'all') return matched.map((indicator) => ({ kind: 'indicator', indicator }));

  const groups = new Map<string, IndicatorOut[]>();
  for (const indicator of matched) {
    const list = groups.get(indicator.category);
    if (list) list.push(indicator);
    else groups.set(indicator.category, [indicator]);
  }
  // Unsearched, the picker's fixed category order; searched, the order the categories' best matches
  // ranked in — so the exact hit's category comes first rather than wherever it sits alphabetically.
  const rank = (c: string) => {
    const i = (CATEGORY_ORDER as readonly string[]).indexOf(c);
    return i === -1 ? CATEGORY_ORDER.length : i;
  };
  const categories = normaliseSearch(query)
    ? [...groups.keys()]
    : [...groups.keys()].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  return categories.flatMap((category): PickerRow[] => [
    { kind: 'header', category },
    ...groups.get(category)!.map((indicator): PickerRow => ({ kind: 'indicator', indicator })),
  ]);
}
